import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { FunctionComponent } from 'react';
import { useAppTranslate } from '../../../../core/hooks';
import { Loader } from '../../../../ui/components';
import { StatisticsTableChartOptions } from '../../../chart-settings/components';
import { useChartSettings } from '../../../chart-settings/hooks';
import { ChartSettings } from '../../../chart-settings/model';
import { BitRateMetric, NetworkServiceBitrateViewModel, ThroughputMonitoredServiceApiModel } from '../../model';
import { useNetworkServiceBitratesApiGet } from '../../hooks';
import { useTimeSeriesStatisticsHighchartsOptions } from '../../../timeseries-statistics/hooks/TimeSeriesStatistics.hook';
import { buildNamedSeriesConfiguration } from '../BitrateStatisticsTableChart/BitrateStatisticsTableChart';
import { formatThroughput } from '../../../../l3-services/model';

interface Props {
  chartTitle: string;
  initialChartSettings: ChartSettings;
  initialChartData: NetworkServiceBitrateViewModel;
  monitoredService: ThroughputMonitoredServiceApiModel;
  metrics: BitRateMetric[];
  unit: string;
  open: boolean;
  onClose: () => void;
  customerId: number | null;
  tooltipPointValueFormatter?: (value?: number) => string;
}

export const BitrateStatisticsTableChartDialog: FunctionComponent<Props> = ({
  chartTitle,
  monitoredService,
  initialChartData,
  metrics,
  unit,
  open,
  onClose,
  initialChartSettings,
  customerId,
  tooltipPointValueFormatter
}) => {
  const chartSettingsInteraction = useChartSettings({
    timespan: initialChartSettings.timespan,
    resolution: initialChartSettings.resolution,
  });
  const chartSettings = chartSettingsInteraction.chartSettings;
  const serviceThroughput = monitoredService.throughput;
  const {
    networkServiceBitrateStatistics,
    isFetched,
    isFetching,
  } = useNetworkServiceBitratesApiGet(
    customerId,
    monitoredService.itemId,
    serviceThroughput,
    chartSettings,
    'chart',
  );
  const translate = useAppTranslate();
  const effectiveChartData = networkServiceBitrateStatistics || initialChartData;
  const seriesConfiguration = metrics
    .map(metric => buildNamedSeriesConfiguration(effectiveChartData, metric, translate('tableHeaders.' + metric)));
  const highchartsOptions = useTimeSeriesStatisticsHighchartsOptions(chartSettings, unit, seriesConfiguration, tooltipPointValueFormatter);

  return (
    <Dialog open={open} onClose={onClose} disableEscapeKeyDown={true} maxWidth={'xl'} fullWidth={true}>
      <Box sx={{ p: { xs: '2rem', sm: '6rem' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 2 }}>
          <Box>
            <Typography variant={'h5'}>{ monitoredService.name } | { translate(chartTitle) } [{ unit }]</Typography>
            <Typography variant={'h6'}>{translate('tableHeaders.sozId')}: { monitoredService.orderNumber } | { formatThroughput(serviceThroughput) }</Typography>
          </Box>
          <Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <StatisticsTableChartOptions chartSettingsInteraction={chartSettingsInteraction} />
        { isFetching && (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader loading={isFetching} />
          </Box>
        )}
        { effectiveChartData && isFetched && !isFetching && (
          <HighchartsReact highcharts={ Highcharts } options={ highchartsOptions } />
        )}
      </Box>
    </Dialog>
  );
};